import { queryOptions } from '@tanstack/react-query';
import pino from 'pino';

import { DashboardRepository } from '../repositories/dashboardRepository';
import { TeamRepository } from '../repositories/teamRepository';
import { queryClient } from '../router/queryClient';

import { currencyAndTotalQuery } from './paymentQuery';
import { userByIdQuery } from './userQuery';

const logger = pino();

const dashboardKeys = {
	getCompanyMetrics: () => ['getCompanyMetrics'] as const,
	getTeamMetrics: (teamId: string) => ['getTeamMetrics', teamId, 'teamId'] as const,
	getManagerMetrics: (teamId: string) => ['getManagerMetrics', teamId, 'teamId'] as const,
	getSettlementsForCompany: (fromDate: string, toDate: string) =>
		['getSettlementsForCompany', fromDate, 'fromDate', toDate, 'toDate'] as const,
	getSettlementsForTeam: (teamId: string, fromDate: string, toDate: string) =>
		['getSettlementsForCompany', teamId, 'teamId', fromDate, 'fromDate', toDate, 'toDate'] as const,
	getSettlementsForTeamManager: (teamId: string, fromDate: string, toDate: string) =>
		['getSettlementsForCompany', teamId, 'teamId', fromDate, 'fromDate', toDate, 'toDate'] as const,
};

const changeOverviewData = async item => {
	try {
		const totalApprovedAmount = await queryClient.ensureQueryData(
			currencyAndTotalQuery(item.currency, item.approvedAmount),
		);
		const totalAvailableBalance = await queryClient.ensureQueryData(
			currencyAndTotalQuery(item.currency, item.availableBalance),
		);
		const totalSpentAmount = await queryClient.ensureQueryData(currencyAndTotalQuery(item.currency, item.spentAmount));
		item.totalApprovedAmount = totalApprovedAmount || '';
		item.totalAvailableBalance = totalAvailableBalance || '';
		item.totalSpentAmount = totalSpentAmount || '';
		return item;
	} catch (error) {
		logger.error({ message: 'Error processing overview data', error });
		throw error;
	}
};

const changeTeamData = async team => {
	try {
		const totalApprovedAmount = await queryClient.ensureQueryData(
			currencyAndTotalQuery(team.metrics[0].currency, team.metrics[0].approvedAmount.toString()),
		);
		const totalAvailableBalance = await queryClient.ensureQueryData(
			currencyAndTotalQuery(team.metrics[0].currency, team.metrics[0].availableBalance.toString()),
		);
		const totalSpentAmount = await queryClient.ensureQueryData(
			currencyAndTotalQuery(team.metrics[0].currency, team.metrics[0].spentAmount.toString()),
		);
		team.totalApprovedAmount = totalApprovedAmount || '';
		team.totalAvailableBalance = totalAvailableBalance || '';
		team.totalSpentAmount = totalSpentAmount || '';
		team.activeCardCount = team.metrics[0].activeCardCount;
		return team;
	} catch (error) {
		logger.error({ message: 'Error processing team data', error });
		throw error;
	}
};

const changePeopleData = async people => {
	try {
		const totalApprovedAmount = await queryClient.ensureQueryData(
			currencyAndTotalQuery(people.metrics[0].currency, people.metrics[0].approvedAmount),
		);
		const totalAvailableBalance = await queryClient.ensureQueryData(
			currencyAndTotalQuery(people.metrics[0].currency, people.metrics[0].availableBalance),
		);
		const totalSpentAmount = await queryClient.ensureQueryData(
			currencyAndTotalQuery(people.metrics[0].currency, people.metrics[0].spentAmount),
		);
		const member = await queryClient.ensureQueryData(userByIdQuery(people.memberId));
		people.totalApprovedAmount = totalApprovedAmount || '';
		people.totalAvailableBalance = totalAvailableBalance || '';
		people.totalSpentAmount = totalSpentAmount || '';
		people.memberName = member.fullName || '';
		people.activeCardCount = people.metrics[0].activeCardCount;
		return people;
	} catch (error) {
		logger.error({ message: 'Error processing people data', error });
		throw error;
	}
};

export const getCompanyMetricsQuery = () => ({
	queryKey: dashboardKeys.getCompanyMetrics(),
	queryFn: async () => {
		const initialMetrics = await DashboardRepository.getCompanyMetrics();

		const overviewPromises = initialMetrics.overview.map(item => changeOverviewData(item));
		const teamsData = await TeamRepository.getTeamsPage({ state: 'active' });
		teamsData?.forEach(team => {
			team.metrics = [
				{
					approvedAmount: 0,
					spentAmount: 0,
					availableBalance: 0,
					currency: '',
					activeCardCount: 0,
				},
			];
			initialMetrics.teams.forEach(t => {
				if (t.teamId === team.id && t.metrics.length > 0) {
					team.metrics = t.metrics;
				}
			});
		});

		const teamsPromises = teamsData?.map(item => changeTeamData(item)) || [];

		const [overview, teams] = await Promise.all([Promise.all(overviewPromises), Promise.all(teamsPromises)]);

		return { overview, teams };
	},
});

export const getTeamMetricsQuery = (teamId: string) => ({
	queryKey: dashboardKeys.getTeamMetrics(teamId),
	queryFn: async () => {
		const initialMetrics = await DashboardRepository.getTeamMetrics(teamId);

		const overviewPromises = initialMetrics.overview.map(item => changeOverviewData(item));
		const peoplePromises = initialMetrics.people?.map(item => changePeopleData(item)) || [];

		const [overview, people] = await Promise.all([Promise.all(overviewPromises), Promise.all(peoplePromises)]);

		return { overview, people };
	},
});

export const getManagerMetricsQuery = (teamId: string) => ({
	queryKey: dashboardKeys.getManagerMetrics(teamId),
	queryFn: async () => {
		const initialMetrics = await DashboardRepository.getManagerMetrics(teamId);

		const overviewPromises = initialMetrics.overview.map(item => changeOverviewData(item));
		const peoplePromises = initialMetrics.people?.map(item => changePeopleData(item)) || [];

		const [overview, people] = await Promise.all([Promise.all(overviewPromises), Promise.all(peoplePromises)]);

		return { overview, people };
	},
});

export const getSettlementsForCompanyQuery = ({ fromDate, toDate }: { fromDate: string; toDate: string }) => {
	return queryOptions({
		queryKey: dashboardKeys.getSettlementsForCompany(fromDate, toDate),
		queryFn: () => DashboardRepository.getSettlementsForCompany(fromDate, toDate),
		enabled: Boolean(fromDate && toDate),
	});
};

export const getSettlementsForTeamQuery = ({
	teamId,
	fromDate,
	toDate,
}: {
	teamId: string;
	fromDate: string;
	toDate: string;
}) =>
	queryOptions({
		queryKey: dashboardKeys.getSettlementsForTeam(teamId, fromDate, toDate),
		queryFn: () => DashboardRepository.getSettlementsForTeam(teamId, fromDate, toDate),
		enabled: Boolean(teamId && fromDate && toDate),
	});

export const getSettlementsForTeamManagerQuery = ({
	teamId,
	fromDate,
	toDate,
}: {
	teamId: string;
	fromDate: string;
	toDate: string;
}) =>
	queryOptions({
		queryKey: dashboardKeys.getSettlementsForTeamManager(teamId, fromDate, toDate),
		queryFn: () => DashboardRepository.getSettlementsForTeamManager(teamId, fromDate, toDate),
		enabled: Boolean(teamId && fromDate && toDate),
	});
